import React, { useState } from 'react';
import { Card as BSCard, Button as BSButton, Collapse as BSCollapse, Badge as BSBadge } from 'react-bootstrap';
import WorkNumberWidget from './WorkNumberDisplay/WorkNumberDisplay';
import ColorSpecialties from '../../ConfigFiles/colors/color_specialties';
import VocabMedicSearch from '../../ConfigFiles/multilingual/VocabMedicSearch';
import { useLanguage } from '../../API/Context/LanguageContext';
import type { MatchingUser } from '../../Types/schema-helpers';


interface MedicCardProps {
  user: MatchingUser;
  isSelected: boolean;
  onSelect: () => void;
}

const MedicCard: React.FC<MedicCardProps> = ({ user, isSelected, onSelect }) => {
  const { lan } = useLanguage();
  const [showSkills, setShowSkills] = useState<boolean>(false);

  const formattedAvailability = user.earliest_availability
    ? new Date(user.earliest_availability).toLocaleDateString(lan === 0 ? 'es-MX' : 'en-US', {
        month: 'short',
        day: 'numeric',
      })
    : null;

  return (
    <BSCard 
      className={`mb-3 shadow-sm border-2 ${isSelected ? 'border-primary bg-light' : 'border-transparent'}`}
      onClick={onSelect}
      style={{ cursor: 'pointer', transition: '0.2s' }}
    >
      <BSCard.Body>
        <div className="d-flex justify-content-between align-items-start">
          <div>
            <h5 className="text-primary fw-bold mb-0">{user.full_name}</h5>
            <small className="text-muted d-block">
              {user.speciality || 'General Medicine'}
              {user.subspeciality ? ` - ${user.subspeciality}` : ''}
            </small>
          </div>
          {formattedAvailability && (
            <BSBadge bg="success" className="p-2">
               {lan === 0 ? 'Disponible: ' : 'Available: '}
               {formattedAvailability}
            </BSBadge>
          )}
        </div>

        <div className="mt-3 text-muted">
          {user.address && (
            <p className="mb-1 small">
              📍 {user.address.location_name ? `${user.address.location_name} - ` : ''}
              {user.address.full_address}
            </p>
          )}
          <WorkNumberWidget contacts={user.contact_info} />
        </div>

        {user.skills && user.skills.length > 0 && (
          <>
            <BSButton 
              variant="link" 
              size="sm" 
              className="ps-0 mt-2 text-decoration-none" 
              onClick={(e: React.MouseEvent) => { 
                e.stopPropagation(); 
                setShowSkills(!showSkills); 
              }}
            >
              {showSkills ? VocabMedicSearch.hide_skills[lan] : VocabMedicSearch.show_skills[lan]}
            </BSButton>

            <BSCollapse in={showSkills}>
              <div className="mt-2">
                {user.skills.map((skill) => (
                  <BSBadge 
                    key={skill.id} 
                    pill 
                    className="me-1" 
                    style={{ backgroundColor: ColorSpecialties[skill.category] || ColorSpecialties[0] }}
                  >
                    {skill.name}
                  </BSBadge>
                ))}
              </div>
            </BSCollapse>
          </>
        )}
      </BSCard.Body>
    </BSCard>
  );
};

export default MedicCard;